import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useSeo } from "@/lib/seo";
import AiImagePicker from "@/components/AiImagePicker";

const API = process.env.REACT_APP_BACKEND_URL;

const fetchJson = async (path) => {
  const r = await fetch(`${API}${path}`, { credentials: "include" });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json();
};

const downloadFile = async (fileId, filename) => {
  const r = await fetch(`${API}/api/project-briefs/files/${fileId}`, { credentials: "include" });
  if (!r.ok) throw new Error(`Download failed (${r.status})`);
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || fileId;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const csvEscape = (val) => {
  if (val === null || val === undefined) return "";
  let s = String(val).replace(/"/g, '""');
  // SEC-003: neutralize spreadsheet formula characters. A lead who submits
  // `=cmd|' /C calc'!A1` in the name field would otherwise trigger a
  // formula when the admin opens the CSV in Excel/Sheets.
  if (/^[=+\-@\t\r]/.test(s)) {
    s = "'" + s;
  }
  return /[",\n\r]/.test(s) ? `"${s}"` : s;
};

const downloadCsv = (filename, rows, headers) => {
  const body = [
    headers.map(csvEscape).join(","),
    ...rows.map((r) => headers.map((h) => csvEscape(r[h])).join(",")),
  ].join("\n");
  const blob = new Blob([body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const AdminLeads = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const initialTab = location.pathname.endsWith("/briefs") ? "briefs" : "enquiries";
  const [tab, setTab] = useState(initialTab);
  const [enquiries, setEnquiries] = useState([]);
  const [catalogues, setCatalogues] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [errors, setErrors] = useState({}); // { enquiries?: string, catalogues?: string, briefs?: string }
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [replyLead, setReplyLead] = useState(null); // enquiry object under reply

  useSeo({
    title: tab === "briefs" ? "Project briefs · Admin" : "Leads · Admin · Asia Sourcing",
    path: tab === "briefs" ? "/admin/briefs" : "/admin/leads",
    noindex: true,
  });

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      // Fetch each source independently so one failure never blanks the others.
      const results = await Promise.allSettled([
        fetchJson("/api/enquiries?limit=500"),
        fetchJson("/api/catalogue-requests?limit=500"),
        fetchJson("/api/project-briefs?limit=500"),
      ]);
      const [e, c, b] = results;
      const errs = {};
      if (e.status === "fulfilled") setEnquiries(Array.isArray(e.value) ? e.value : []);
      else errs.enquiries = e.reason?.message || "Could not load enquiries.";
      if (c.status === "fulfilled") setCatalogues(Array.isArray(c.value) ? c.value : []);
      else errs.catalogues = c.reason?.message || "Could not load catalogue requests.";
      if (b.status === "fulfilled") setBriefs(Array.isArray(b.value) ? b.value : []);
      else errs.briefs = b.reason?.message || "Could not load project briefs.";
      setErrors(errs);
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    const list = tab === "enquiries" ? enquiries : tab === "catalogues" ? catalogues : briefs;
    if (!q.trim()) return list;
    const needle = q.trim().toLowerCase();
    return list.filter((it) =>
      Object.values(it)
        .filter((v) => typeof v === "string")
        .some((v) => v.toLowerCase().includes(needle))
    );
  }, [tab, q, enquiries, catalogues, briefs]);

  const exportCsv = () => {
    if (tab === "enquiries") {
      downloadCsv(
        `enquiries-${new Date().toISOString().slice(0, 10)}.csv`,
        enquiries,
        ["created_at", "name", "company", "email", "phone", "category", "moq", "message", "email_sent", "id"]
      );
    } else if (tab === "catalogues") {
      downloadCsv(
        `catalogues-${new Date().toISOString().slice(0, 10)}.csv`,
        catalogues,
        ["created_at", "name", "company", "email", "phone", "category_id", "category_name", "email_sent", "id"]
      );
    } else {
      downloadCsv(
        `briefs-${new Date().toISOString().slice(0, 10)}.csv`,
        briefs.map((b) => ({
          ...b,
          file_count: (b.reference_files || []).length,
          file_names: (b.reference_files || []).map((f) => f.name || f.original_filename).join(" | "),
        })),
        ["created_at", "name", "company", "email", "phone", "category", "quantity", "target_price", "materials", "destination", "timeline", "reference_notes", "file_count", "file_names", "email_sent", "id"]
      );
    }
  };

  return (
    <main style={{ background: "var(--paper)", minHeight: "100svh" }} data-testid="admin-leads-page">
      {/* Header */}
      <header
        style={{
          background: "var(--ink)",
          color: "var(--paper)",
          padding: "1.4rem 1.6rem 1.2rem",
          borderBottom: "1px solid var(--line)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "1rem",
            flexWrap: "wrap",
          }}
        >
          <div>
            <p
              className="mono"
              style={{ color: "var(--brass)", letterSpacing: ".12em", marginBottom: ".3rem" }}
            >
              Asia Sourcing · Admin
            </p>
            <h1
              className="display"
              style={{ fontSize: "1.6rem", margin: 0, letterSpacing: "-.01em" }}
            >
              Leads &amp; catalogue downloads
            </h1>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <span
              className="mono"
              style={{ color: "rgba(239,234,224,.62)", fontSize: ".68rem" }}
              data-testid="admin-user-email"
            >
              {user?.email}
            </span>
            <Link
              to="/"
              className="btn btn-ghost"
              style={{ borderColor: "rgba(239,234,224,.2)", color: "rgba(239,234,224,.8)" }}
            >
              ← Site
            </Link>
            <button
              type="button"
              onClick={logout}
              className="btn btn-ghost"
              style={{ borderColor: "rgba(239,234,224,.2)", color: "rgba(239,234,224,.8)" }}
              data-testid="admin-logout"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Tabs + actions */}
      <section
        style={{
          padding: "1.2rem 1.6rem",
          borderBottom: "1px solid var(--line)",
          background: "var(--paper-raised)",
          display: "flex",
          gap: "1rem",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", gap: ".4rem" }}>
          <TabButton active={tab === "enquiries"} onClick={() => setTab("enquiries")} testid="tab-enquiries">
            Enquiries <Count n={enquiries.length} />
          </TabButton>
          <TabButton active={tab === "catalogues"} onClick={() => setTab("catalogues")} testid="tab-catalogues">
            Catalogue downloads <Count n={catalogues.length} />
          </TabButton>
          <TabButton active={tab === "briefs"} onClick={() => setTab("briefs")} testid="tab-briefs">
            Project briefs <Count n={briefs.length} />
          </TabButton>
        </div>
        <input
          type="search"
          placeholder="Search name, company, email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="admin-search"
          style={{
            flex: "1 1 240px",
            minWidth: "200px",
            background: "var(--paper)",
            border: "1px solid var(--line)",
            padding: ".6rem .85rem",
            fontFamily: "var(--display)",
            fontSize: ".95rem",
            color: "var(--ink)",
          }}
        />
        <button
          type="button"
          onClick={exportCsv}
          className="btn btn-fill"
          data-testid="admin-export-csv"
        >
          <span>Export CSV →</span>
        </button>
      </section>

      {/* Body */}
      <section style={{ padding: "1.5rem 1.6rem" }}>
        {loading && (
          <p className="mono" style={{ color: "var(--brass-deep)" }} data-testid="admin-loading">
            Loading…
          </p>
        )}
        {err && (
          <p className="mono" style={{ color: "#a44" }} data-testid="admin-error">
            {err}
          </p>
        )}
        {!loading && errors[tab] && (
          <p
            className="mono"
            style={{
              color: "#a44",
              padding: ".8rem 1rem",
              border: "1px solid rgba(170,68,68,.3)",
              background: "rgba(170,68,68,.06)",
              marginBottom: "1rem",
            }}
            data-testid={`admin-tab-error-${tab}`}
          >
            Could not load {tab}: {errors[tab]}
          </p>
        )}
        {!loading && !errors[tab] && filtered.length === 0 && (
          <p
            style={{ color: "var(--ink-soft)", fontStyle: "italic" }}
            data-testid="admin-empty"
          >
            No {tab} yet.
          </p>
        )}
        {!loading && !errors[tab] && filtered.length > 0 && (
          <div style={{ overflowX: "auto", border: "1px solid var(--line)", borderRadius: 2 }}>
            <table
              style={{ width: "100%", borderCollapse: "collapse", fontSize: ".88rem" }}
              data-testid="admin-table"
            >
              {tab === "enquiries" ? (
                <EnquiryTable rows={filtered} onReply={setReplyLead} />
              ) : tab === "catalogues" ? (
                <CatalogueTable rows={filtered} />
              ) : (
                <BriefTable rows={filtered} />
              )}
            </table>
          </div>
        )}
      </section>
      {replyLead && (
        <LeadReplyDrawer lead={replyLead} onClose={() => setReplyLead(null)} adminEmail={user?.email} />
      )}
    </main>
  );
};

const Count = ({ n }) => (
  <span style={{ opacity: 0.55, fontSize: ".7em", marginLeft: ".4em" }}>({n})</span>
);

const TabButton = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    style={{
      background: active ? "var(--ink)" : "transparent",
      color: active ? "var(--paper)" : "var(--ink)",
      border: "1px solid var(--ink)",
      padding: ".55rem 1rem",
      fontFamily: "var(--mono)",
      fontSize: ".7rem",
      letterSpacing: ".1em",
      textTransform: "uppercase",
      cursor: "pointer",
      borderRadius: 2,
    }}
  >
    {children}
  </button>
);

const ThStyle = {
  textAlign: "left",
  padding: ".7rem .9rem",
  background: "var(--paper-raised)",
  borderBottom: "1px solid var(--line)",
  color: "var(--brass-deep)",
  fontFamily: "var(--mono)",
  fontSize: ".62rem",
  letterSpacing: ".1em",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
};
const TdStyle = {
  padding: ".7rem .9rem",
  borderBottom: "1px solid var(--line)",
  verticalAlign: "top",
  color: "var(--ink)",
};

const EnquiryTable = ({ rows, onReply }) => (
  <>
    <thead>
      <tr>
        <th style={ThStyle}>Received</th>
        <th style={ThStyle}>Name</th>
        <th style={ThStyle}>Company</th>
        <th style={ThStyle}>Email</th>
        <th style={ThStyle}>Phone</th>
        <th style={ThStyle}>Category · MOQ</th>
        <th style={ThStyle}>Message</th>
        <th style={ThStyle}>Email</th>
        <th style={ThStyle}>Reply</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.id} data-testid={`enquiry-row-${r.id}`}>
          <td style={TdStyle}>{formatDate(r.created_at)}</td>
          <td style={TdStyle}><b>{r.name}</b></td>
          <td style={TdStyle}>{r.company || "—"}</td>
          <td style={TdStyle}><a href={`mailto:${r.email}`}>{r.email}</a></td>
          <td style={TdStyle}>{r.phone || "—"}</td>
          <td style={TdStyle}>
            <div>{r.category}</div>
            <div style={{ color: "var(--brass-deep)", fontFamily: "var(--mono)", fontSize: ".7rem" }}>
              {r.moq || "—"}
            </div>
          </td>
          <td style={{ ...TdStyle, maxWidth: "32ch" }}>{r.message}</td>
          <td style={TdStyle}>
            <span className="mono" style={{ color: r.email_sent ? "#3a7" : "var(--ink-soft)" }}>
              {r.email_sent ? "sent" : "skipped"}
            </span>
          </td>
          <td style={TdStyle}>
            <button
              type="button"
              onClick={() => onReply?.(r)}
              data-testid={`enquiry-reply-${r.id}`}
              className="btn btn-ghost"
              style={{ padding: ".25rem .65rem", fontSize: ".78rem" }}
            >
              Reply
            </button>
          </td>
        </tr>
      ))}
    </tbody>
  </>
);

const CatalogueTable = ({ rows }) => (
  <>
    <thead>
      <tr>
        <th style={ThStyle}>Downloaded</th>
        <th style={ThStyle}>Name</th>
        <th style={ThStyle}>Company</th>
        <th style={ThStyle}>Email</th>
        <th style={ThStyle}>Phone</th>
        <th style={ThStyle}>Catalogue</th>
        <th style={ThStyle}>Email</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.id} data-testid={`catalogue-row-${r.id}`}>
          <td style={TdStyle}>{formatDate(r.created_at)}</td>
          <td style={TdStyle}><b>{r.name}</b></td>
          <td style={TdStyle}>{r.company || "—"}</td>
          <td style={TdStyle}><a href={`mailto:${r.email}`}>{r.email}</a></td>
          <td style={TdStyle}>{r.phone || "—"}</td>
          <td style={TdStyle}>{r.category_name}</td>
          <td style={TdStyle}>
            <span className="mono" style={{ color: r.email_sent ? "#3a7" : "var(--ink-soft)" }}>
              {r.email_sent ? "sent" : "skipped"}
            </span>
          </td>
        </tr>
      ))}
    </tbody>
  </>
);

const BriefTable = ({ rows }) => (
  <>
    <thead>
      <tr>
        <th style={ThStyle}>Submitted</th>
        <th style={ThStyle}>Buyer</th>
        <th style={ThStyle}>Contact</th>
        <th style={ThStyle}>Category</th>
        <th style={ThStyle}>Requirements</th>
        <th style={ThStyle}>Destination</th>
        <th style={ThStyle}>Files</th>
        <th style={ThStyle}>Email</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.id} data-testid={`brief-row-${r.id}`}>
          <td style={TdStyle}>
            <div>{formatDate(r.created_at)}</div>
            <div className="mono" style={{ fontSize: ".68rem", color: "var(--ink-soft)", marginTop: ".2rem" }}>
              ref {r.id?.slice(0, 8).toUpperCase()}
            </div>
          </td>
          <td style={TdStyle}>
            <b>{r.name}</b>
            <div style={{ color: "var(--ink-soft)", fontSize: ".82rem", marginTop: ".2rem" }}>
              {r.company}
              {r.website ? (
                <>
                  {" · "}
                  <a href={r.website} target="_blank" rel="noreferrer">site</a>
                </>
              ) : null}
            </div>
          </td>
          <td style={TdStyle}>
            <a href={`mailto:${r.email}`}>{r.email}</a>
            {r.phone && (
              <div className="mono" style={{ fontSize: ".78rem", color: "var(--ink-soft)", marginTop: ".2rem" }}>
                {r.phone}
              </div>
            )}
          </td>
          <td style={TdStyle}><b>{r.category}</b></td>
          <td style={TdStyle}>
            <div>{r.quantity} <span style={{ color: "var(--ink-soft)" }}>· {r.target_price || "price TBD"}</span></div>
            {r.materials && <div style={{ color: "var(--ink-soft)", fontSize: ".82rem", marginTop: ".25rem" }}>{r.materials}</div>}
            {r.reference_notes && (
              <div style={{ color: "var(--ink-soft)", fontSize: ".78rem", marginTop: ".35rem", maxWidth: "34ch" }}>
                <em>&ldquo;{r.reference_notes.length > 140 ? r.reference_notes.slice(0, 140) + "…" : r.reference_notes}&rdquo;</em>
              </div>
            )}
          </td>
          <td style={TdStyle}>
            <div>{r.destination}</div>
            {r.timeline && (
              <div className="mono" style={{ fontSize: ".72rem", color: "var(--brass-deep)", marginTop: ".2rem" }}>
                {r.timeline}
              </div>
            )}
          </td>
          <td style={TdStyle}>
            {(r.reference_files || []).length === 0 ? (
              <span style={{ color: "var(--ink-soft)" }}>—</span>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: ".3rem" }}>
                {r.reference_files.map((f, i) => (
                  <li key={`${r.id}-f-${i}`}>
                    {f.id ? (
                      <button
                        type="button"
                        onClick={() =>
                          downloadFile(f.id, f.name || f.original_filename || "file").catch((e) => alert(e.message))
                        }
                        style={{
                          background: "transparent",
                          border: 0,
                          padding: 0,
                          color: "var(--brass-deep)",
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontFamily: "inherit",
                          fontSize: ".84rem",
                          textAlign: "left",
                        }}
                        data-testid={`brief-file-${f.id}`}
                      >
                        {f.name || f.original_filename || "file"} ↓
                      </button>
                    ) : (
                      <span style={{ color: "var(--ink-soft)", fontSize: ".82rem" }}>
                        {f.name || "file"}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </td>
          <td style={TdStyle}>
            <span className="mono" style={{ color: r.email_sent ? "#3a7" : "var(--ink-soft)" }}>
              {r.email_sent ? "sent" : "skipped"}
            </span>
          </td>
        </tr>
      ))}
    </tbody>
  </>
);

export default AdminLeads;

// ─────────────────────────────────────────────────────────────
// Sprint 14 · Lead reply drawer — inline image via AiImagePicker
// ─────────────────────────────────────────────────────────────
const LeadReplyDrawer = ({ lead, onClose, adminEmail }) => {
  const [subject, setSubject] = useState(`Re: your enquiry · ${lead.category || "sourcing"}`);
  const [body, setBody] = useState(
    `Hi ${lead.name || "there"},\n\nThank you for your enquiry regarding ${lead.category || "your sourcing programme"}.\n\n— Asia Sourcing`
  );
  const [image, setImage] = useState(null); // {id, url, prompt}
  const [imageAlt, setImageAlt] = useState("");
  const [showPicker, setShowPicker] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(null); // response object

  const handleSelect = (img) => {
    setImage(img);
    setImageAlt(img.prompt?.slice(0, 200) || `Asia Sourcing ${lead.category || "programme"}`);
    setShowPicker(false);
  };

  const doSend = async () => {
    setError(""); setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/leads/reply`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to_email: lead.email,
          subject: subject.trim(),
          body_text: body,
          image_id: image?.id || null,
          image_alt: imageAlt || null,
          lead_id: lead.id,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: `HTTP ${r.status}` }));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      setSent(await r.json());
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const previewHtmlSrc = image ? `${API}${image.url}` : null;

  return (
    <div
      data-testid="lead-reply-drawer"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(26,23,20,.55)", zIndex: 900, display: "flex", justifyContent: "flex-end" }}
    >
      <aside style={{ width: "min(720px, 96vw)", background: "var(--paper)", height: "100%", overflow: "auto", borderLeft: "1px solid var(--line)", boxShadow: "-20px 0 60px rgba(0,0,0,.3)" }}>
        <header style={{ padding: "1rem 1.4rem", borderBottom: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--paper-raised)" }}>
          <div>
            <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: 0 }}>Reply to buyer</p>
            <h3 style={{ fontFamily: "var(--display)", fontSize: "1.1rem", margin: ".2rem 0 0" }}>{lead.name} · {lead.email}</h3>
          </div>
          <button type="button" onClick={onClose} className="btn btn-ghost" data-testid="lead-reply-close" style={{ padding: ".4rem .9rem" }}>Close</button>
        </header>

        {sent ? (
          <div style={{ padding: "1.6rem 1.4rem" }} data-testid="lead-reply-sent">
            <div style={{ padding: "1rem 1.2rem", background: "rgba(83,110,64,.08)", border: "1px solid #556e40", color: "#3a4f2b" }}>
              <p className="mono" style={{ fontSize: ".7rem", letterSpacing: ".1em", textTransform: "uppercase", margin: 0 }}>Sent · Resend id {sent.resend_id?.slice(0, 8) || "—"}</p>
              <p style={{ margin: ".4rem 0 0", fontSize: ".95rem" }}>The reply was dispatched via Resend to <b>{lead.email}</b>.</p>
              {sent.image_url && (
                <p style={{ margin: ".4rem 0 0", fontSize: ".85rem" }}>Image URL: <a href={sent.image_url} target="_blank" rel="noreferrer" style={{ color: "#8A6526" }}>{sent.image_url}</a></p>
              )}
            </div>
            <button type="button" onClick={onClose} className="btn btn-primary" style={{ marginTop: "1.2rem" }} data-testid="lead-reply-done">Done</button>
          </div>
        ) : (
          <div style={{ padding: "1.2rem 1.4rem" }}>
            {/* Enquiry preview */}
            <details style={{ marginBottom: "1rem", border: "1px solid var(--line)", padding: ".6rem .8rem", background: "var(--paper-raised)" }}>
              <summary style={{ cursor: "pointer", fontSize: ".85rem", color: "var(--ink-soft)" }}>Original enquiry</summary>
              <p style={{ margin: ".5rem 0 0", whiteSpace: "pre-wrap", fontSize: ".88rem" }}>{lead.message}</p>
            </details>

            <label style={{ display: "block", marginBottom: ".8rem" }}>
              <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .3rem" }}>Subject</p>
              <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="lead-reply-subject" style={inputStyle} />
            </label>

            <label style={{ display: "block", marginBottom: ".8rem" }}>
              <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .3rem" }}>Body</p>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={9} data-testid="lead-reply-body" style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit", lineHeight: 1.55 }} />
            </label>

            {/* Image attachment */}
            <div style={{ marginBottom: "1rem", border: "1px dashed var(--line)", padding: ".8rem", background: "var(--paper-raised)" }}>
              <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .5rem" }}>Attach image (optional)</p>
              {image ? (
                <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}>
                  <img src={previewHtmlSrc} alt={imageAlt} style={{ width: 140, height: 100, objectFit: "cover", border: "1px solid var(--line)" }} data-testid="lead-reply-image-preview" />
                  <div style={{ flex: 1 }}>
                    <label style={{ display: "block", marginBottom: ".4rem" }}>
                      <p className="mono" style={{ fontSize: ".58rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .25rem" }}>Alt text</p>
                      <input type="text" value={imageAlt} onChange={(e) => setImageAlt(e.target.value)} data-testid="lead-reply-image-alt" style={inputStyle} />
                    </label>
                    <div style={{ display: "flex", gap: ".4rem" }}>
                      <button type="button" onClick={() => setShowPicker(true)} className="btn btn-ghost" style={{ padding: ".25rem .7rem", fontSize: ".8rem" }} data-testid="lead-reply-image-replace">Replace</button>
                      <button type="button" onClick={() => setImage(null)} className="btn btn-ghost" style={{ padding: ".25rem .7rem", fontSize: ".8rem" }} data-testid="lead-reply-image-remove">Remove</button>
                    </div>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => setShowPicker(true)} className="btn btn-ghost" style={{ padding: ".4rem 1rem" }} data-testid="lead-reply-image-add">Generate image →</button>
              )}
            </div>

            {/* Outbound preview */}
            <details open style={{ marginBottom: "1rem" }}>
              <summary className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", cursor: "pointer", marginBottom: ".4rem" }}>Outbound preview</summary>
              <div data-testid="lead-reply-preview" style={{ border: "1px solid var(--line)", padding: "1rem 1.2rem", background: "#F6F2E9", fontSize: ".9rem", color: "#1A1714" }}>
                <p style={{ margin: 0, fontSize: ".78rem", color: "#8A6526", fontFamily: "monospace" }}>To: {lead.email} · From: Asia Sourcing</p>
                <p style={{ margin: ".4rem 0 .8rem", fontWeight: 600 }}>{subject}</p>
                <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{body}</div>
                {image && (
                  <div style={{ margin: "1rem 0", textAlign: "center" }}>
                    <img src={previewHtmlSrc} alt={imageAlt} style={{ maxWidth: "100%", maxHeight: 260, border: "1px solid rgba(26,23,20,.12)" }} />
                  </div>
                )}
              </div>
            </details>

            {error && <p data-testid="lead-reply-error" className="mono" style={{ color: "#a44", fontSize: ".85rem", marginBottom: ".8rem" }}>{error}</p>}

            <label style={{ display: "flex", alignItems: "center", gap: ".5rem", marginBottom: "1rem", fontSize: ".85rem" }}>
              <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="lead-reply-confirm" />
              <span>I have reviewed the outbound email and confirm it should be sent to <b>{lead.email}</b> as <b>{adminEmail}</b>.</span>
            </label>
            <button type="button" onClick={doSend} disabled={!confirm || busy || subject.trim().length < 2 || body.trim().length < 2} className="btn btn-primary" data-testid="lead-reply-send" style={{ padding: ".7rem 1.4rem", fontSize: ".95rem" }}>
              {busy ? "Sending…" : "Send reply via Resend →"}
            </button>
          </div>
        )}
      </aside>

      {showPicker && (
        <AiImagePicker
          contextLabel="Lead reply"
          onClose={() => setShowPicker(false)}
          onSelect={handleSelect}
          defaultPrompt={lead.category ? `Editorial hero image for a ${lead.category} sourcing programme — quiet-luxury, natural morning light, no text.` : ""}
        />
      )}
    </div>
  );
};

const inputStyle = { width: "100%", background: "var(--paper)", border: "1px solid var(--line)", padding: ".55rem .75rem", fontFamily: "var(--display)", fontSize: ".92rem", color: "var(--ink)" };
